import { matchPath, Navigate, Outlet, useLocation } from 'react-router-dom';
import {
  resolveCapabilities,
  type InstanceCapabilities,
} from '../instances/capabilities';
import { useInstance } from '../instances/useInstance';

// Case-insensitive like the router itself (`matchPath` defaults to
// caseSensitive: false), so /Library cannot slip past the guard.
const isUnderPath = (pathname: string, base: string) =>
  Boolean(matchPath(`${base}/*`, pathname));

const isAtPath = (pathname: string, pattern: string) =>
  Boolean(matchPath(pattern, pathname));

/**
 * The router matches routes against the decoded pathname while
 * `useLocation().pathname` keeps the address as typed, so `/%6Cibrary` opens
 * the library route. Decide on the same string the router does; a malformed
 * escape is left as is (the router shows its own error for it).
 */
const decodePathname = (pathname: string): string => {
  try {
    return decodeURIComponent(pathname);
  } catch {
    return pathname;
  }
};

/**
 * The free-study library's sections: every address the nav hides under
 * `studyLibrary: 'none'` (ORA-1062). Whole subtrees.
 */
const STUDY_LIBRARY_SECTIONS = [
  '/videos',
  '/qbank-start',
  '/flashcards',
  '/flashcard-decks',
  '/upload-deck',
];

/**
 * The library's own landing pages. An article (`/library/<id>`) stays open:
 * a term pop-up in a course question and an audio-overview transcript open
 * one from inside the student's own content. A course assessment step opens
 * `/self-assessments/<slug>` and its block route (CourseAssessmentStep), so
 * only the landing page and the per-exam drill-in are the library's.
 */
const STUDY_LIBRARY_PAGES = [
  '/library',
  '/self-assessments',
  '/self-assessments/exam/*',
];

/**
 * Pricing stays hidden under `commerce: 'partner'`. Games follow commerce
 * unless the instance explicitly enables them. Billing is refused by Account.
 */
const ORA_COMMERCE_SECTIONS = ['/pricing'];

/**
 * True when the student's instance switches this address off: the library
 * for `studyLibrary: 'none'`, pricing for partner commerce, and Games unless
 * commerce or the Games capability enables them.
 * The Default instance (and a signed-out visitor, who resolves to it) is
 * never redirected.
 */
export const isRouteOffForInstance = (
  rawPathname: string,
  capabilities: InstanceCapabilities
): boolean => {
  const pathname = decodePathname(rawPathname);

  if (
    capabilities.studyLibrary === 'none' &&
    (STUDY_LIBRARY_SECTIONS.some((base) => isUnderPath(pathname, base)) ||
      STUDY_LIBRARY_PAGES.some((pattern) => isAtPath(pathname, pattern)))
  ) {
    return true;
  }

  if (
    capabilities.commerce === 'partner' &&
    ORA_COMMERCE_SECTIONS.some((base) => isUnderPath(pathname, base))
  ) {
    return true;
  }

  if (
    capabilities.commerce === 'partner' &&
    capabilities.games !== 'on' &&
    isUnderPath(pathname, '/games')
  ) {
    return true;
  }

  return false;
};

/**
 * Pathless layout route around the sections an instance can switch off. The
 * nav already hides them; this keeps a typed or linked address from mounting
 * the page: the redirect replaces the page element, so the page's own mount
 * effects (fetches, search-param writes) never run. Same shape as
 * ExamFeatureRouteGuard, which it nests with for /videos and /games.
 */
const InstanceCapabilityRouteGuard = () => {
  const { pathname } = useLocation();
  const { capabilities } = useInstance();

  if (isRouteOffForInstance(pathname, resolveCapabilities(capabilities))) {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
};

export default InstanceCapabilityRouteGuard;
